import { contextWorkspaceHash, type AcquisitionContext } from "@dj/site-navigation/acquisition-context";

import {
  reactWebLogoutRequestQueryParam,
  viewerAuthenticatedCookieName,
  viewerAvatarUrlCookieName,
  viewerDisplayNameCookieName,
  viewerMembershipPlanCookieName,
} from "@/lib/viewerSession";
import { normalizeLocale, type LandingLocale } from "@/i18n/routing";

/**
 * Base URL for the CRA `react-web` app (sign-in / sign-up hash routes).
 * Default matches CRA dev server while landing-page runs on 3001.
 */
export function getReactWebBaseUrl(): string {
  const raw = process.env.NEXT_PUBLIC_REACT_WEB_URL?.trim();
  if (raw) return raw.replace(/\/+$/, "");
  return "http://localhost:3000";
}

export function toReactWebAuthLocale(locale: string): LandingLocale {
  return normalizeLocale(locale);
}

export function reactWebAuthHref(
  locale: string,
  route: "login" | "signup",
  options?: Readonly<{ returnTo?: string | null }>,
): string {
  const l = toReactWebAuthLocale(locale);
  const base = `${getReactWebBaseUrl()}/#/${l}/${route}`;
  const returnTo = options?.returnTo?.trim();
  if (!returnTo) {
    return base;
  }
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}returnTo=${encodeURIComponent(returnTo)}`;
}

export type ReactWebAppRoute =
  | "home"
  | "dashboard"
  | "directory"
  | "skyrules"
  | "my-profile"
  | "settings"
  | "login"
  | "signup";

export function reactWebAppHref(locale: string, route: ReactWebAppRoute): string {
  const l = toReactWebAuthLocale(locale);
  return `${getReactWebBaseUrl()}/#/${l}/${route}`;
}

export type ReactWebDirectoryViewerListIntent = "save" | "compare";

/** Survives Auth0 redirects when hash query params are stripped. */
export const reactWebDirectoryViewerListIntentStorageKey = "dronejob:directoryViewerListIntent";

export function reactWebDirectoryIntentHref(
  locale: string,
  slug: string,
  intent: ReactWebDirectoryViewerListIntent,
): string {
  const l = toReactWebAuthLocale(locale);
  const normalizedSlug = slug.trim().toLowerCase();
  if (typeof window !== "undefined" && normalizedSlug) {
    window.sessionStorage.setItem(
      reactWebDirectoryViewerListIntentStorageKey,
      JSON.stringify({ intent, slug: normalizedSlug }),
    );
  }
  return `${getReactWebBaseUrl()}/#/${l}/directory/${encodeURIComponent(normalizedSlug)}?intent=${intent}`;
}

function clearViewerPresentationCookies(): void {
  if (typeof document === "undefined") return;
  const sharedCookieDomain = process.env.NEXT_PUBLIC_SHARED_COOKIE_DOMAIN?.trim();
  for (const name of [
    viewerAuthenticatedCookieName,
    viewerDisplayNameCookieName,
    viewerAvatarUrlCookieName,
    viewerMembershipPlanCookieName,
  ]) {
    const parts = [`${name}=`, "path=/", "max-age=0", "SameSite=Lax"];
    if (sharedCookieDomain) {
      parts.push(`Domain=${sharedCookieDomain}`);
    }
    if (window.location.protocol === "https:") {
      parts.push("Secure");
    }
    document.cookie = parts.join("; ");
  }
}

/** Cross-app logout handoff consumed by react-web on next load. */
export const reactWebLogoutRequestStorageKey = "dronejob:requestLogout";

export function requestReactWebLogout(locale: string): void {
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(reactWebLogoutRequestStorageKey, "1");
    clearViewerPresentationCookies();
  }
  const logoutUrl = new URL(reactWebAppHref(locale, "home"));
  logoutUrl.searchParams.set(reactWebLogoutRequestQueryParam, "1");
  window.location.assign(logoutUrl.toString());
}

export function acquisitionSignupHref(locale: string, context: AcquisitionContext): string {
  return reactWebAuthHref(locale, "signup", { returnTo: contextWorkspaceHash(locale, context) });
}

/** For action controls that cannot use anchors (e.g. Save/Compare handoffs). */
export function openReactWebInNewTab(href: string): void {
  window.open(href, "_blank", "noopener,noreferrer");
}
